import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, serverTimestamp, query, where, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { uploadLibraryFile } from '../../services/libraryService';
import { fetchCurriculumTopics, saveCurriculumTopic, deleteCurriculumTopic } from '../../services/curriculumEditorService';
import { 
  ALL_AVAILABLE_SUBJECT_IDS, 
  AVAILABLE_SUBJECTS,
  SUBJECT_NAMES, 
  getSubjectClass, 
  getSubjectName, 
  formatSubject 
} from '../../utils/constants';
import { 
  MdLibraryBooks, 
  MdTextFields, 
  MdOndemandVideo, 
  MdPictureAsPdf,
  MdSlideshow,
  MdCheckCircle,
  MdCloudUpload,
  MdClose,
  MdEdit,
  MdDelete,
  MdSave,
  MdCancel,
  MdHistory,
  MdSchool,
  MdMenuBook,
  MdCalendarToday,
  MdSearch,
  MdExpandMore,
  MdExpandLess,
  MdAutoAwesome,
  MdTableChart
} from 'react-icons/md';
import AIDocumentImporterModal from '../../components/teacher/AIDocumentImporterModal';
import AITopicLessonSplitterModal from '../../components/teacher/AITopicLessonSplitterModal';
import { extractDocumentText, fileToBase64, chunkLessonContent } from '../../services/aiService';

// Helpers to fix raw NERDC PDF typos and format compound syllabus titles
const cleanTopicTitle = (rawTitle) => {
  if (!rawTitle) return '';
  return rawTitle
    .replace(/Compositio\s*nSkills/gi, 'Composition Skills')
    .replace(/Developme\s*nt/gi, 'Development')
    .replace(/Developmen\s*t/gi, 'Development')
    .replace(/Comprehens\s*ion/gi, 'Comprehension')
    .replace(/Comprehen\s*ding/gi, 'Comprehending')
    .replace(/Grammar:\s*/gi, 'Grammar: ')
    .replace(/Vocabulary\s*Developme\s*nt:\s*/gi, 'Vocabulary: ')
    .replace(/Spoken\s*English:\s*/gi, 'Spoken English: ')
    .replace(/Continuous\s*Writing:\s*/gi, 'Writing: ')
    .replace(/Listening\s*Comprehens\s*ion:\s*/gi, 'Listening: ')
    .replace(/Reading\s*Comprehens\s*ion:\s*/gi, 'Reading: ')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const isCalendarNotice = (title) => {
  if (!title) return false;
  const l = title.toLowerCase();
  return (
    l.includes('midterm break') ||
    l.includes('mid-term break') ||
    l.includes('examination and closing') ||
    l.includes('examination & closing') ||
    l.includes('revision, examination') ||
    l.includes('midterm examination')
  );
};

const KNOWN_SUBJECTS_FILTER = new Set([
  'mathematics', 'math', 'maths', 'further maths', 'fmaths', 'further mathematics',
  'english', 'english language', 'english studies',
  'physics', 'chemistry', 'biology', 'agric', 'agricultural science',
  'economics', 'accounting', 'financial accounting', 'commerce',
  'government', 'literature', 'literature in english', 'geography',
  'christian religious studies', 'crs', 'crk', 'islamic studies', 'irs', 'irk',
  'civic education', 'social studies', 'social and citizenship studies',
  'physical and health education', 'phe', 'digital technology', 'digital technologies',
  'computer studies', 'ict', 'basic technology', 'basic science', 'intermediate science',
  'cultural and creative arts', 'cultural and creative arts (cca)', 'cca',
  'french', 'business studies', 'trade subjects', 'nigerian history', 'history', 'home economics'
]);

const isSubjectTitle = (str) => {
  if (!str) return false;
  return KNOWN_SUBJECTS_FILTER.has(str.toLowerCase().trim());
};

const parseTopicParts = (title) => {
  const cleaned = cleanTopicTitle(title);
  const parts = [];
  const regex = /(Grammar|Vocabulary|Spoken English|Reading|Listening|Writing|Composition):\s*([^:]+?)(?=(Grammar|Vocabulary|Spoken English|Reading|Listening|Writing|Composition):|$)/gi;
  let match;
  while ((match = regex.exec(cleaned)) !== null) {
    parts.push({
      strand: match[1].trim(),
      content: match[2].trim()
    });
  }
  if (parts.length > 0) {
    return {
      mainTitle: `${parts[0].strand}: ${parts[0].content}`,
      strands: parts.slice(1).map(p => `${p.strand}: ${p.content}`)
    };
  }
  return {
    mainTitle: cleaned,
    strands: []
  };
};

const TeacherUploadLesson = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const defaultSub = location.state?.defaultSubject;

  const isAdmin = userProfile?.role === 'admin';
  const subjects = (isAdmin || !userProfile?.assignedSubjects || userProfile.assignedSubjects.length === 0)
    ? ALL_AVAILABLE_SUBJECT_IDS
    : userProfile.assignedSubjects;

  // Available classes: all 7 for Admin, or only assigned classes for Teacher
  const availableClasses = useMemo(() => {
    if (isAdmin || !userProfile?.assignedSubjects?.length) {
      return ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3', 'UTME'];
    }
    const classSet = new Set(userProfile.assignedSubjects.map(s => getSubjectClass(s).toUpperCase()));
    return Array.from(classSet);
  }, [isAdmin, userProfile]);

  // Selected Class
  const [selectedClass, setSelectedClass] = useState(() => {
    if (defaultSub) return getSubjectClass(defaultSub).toUpperCase();
    return 'SS1';
  });

  // Subjects available strictly for the selected Class
  const availableSubjectsForClass = useMemo(() => {
    const list = AVAILABLE_SUBJECTS[selectedClass] || [];
    if (isAdmin || !userProfile?.assignedSubjects?.length) {
      return list;
    }
    return list.filter(subId => userProfile.assignedSubjects.includes(subId));
  }, [selectedClass, isAdmin, userProfile]);

  // Selected Subject (defaults to first subject of chosen class)
  const [selectedSubject, setSelectedSubject] = useState(() => {
    if (defaultSub) return defaultSub;
    const initialList = AVAILABLE_SUBJECTS['SS1'] || [];
    return initialList[0] || 'math_ss1';
  });

  // Selected Term
  const [term, setTerm] = useState('Term 1');

  // Handle Class Switch: switch class and pick first subject of that class
  const handleClassChange = (newClass) => {
    setSelectedClass(newClass);
    const newSubjects = AVAILABLE_SUBJECTS[newClass] || [];
    const validSubjects = (isAdmin || !userProfile?.assignedSubjects?.length)
      ? newSubjects
      : newSubjects.filter(subId => userProfile.assignedSubjects.includes(subId));
    if (validSubjects.length > 0 && !validSubjects.includes(selectedSubject)) {
      setSelectedSubject(validSubjects[0]);
    }
  };

  // Sync if route state passes a default subject
  useEffect(() => {
    if (defaultSub) {
      const cls = getSubjectClass(defaultSub).toUpperCase();
      setSelectedClass(cls);
      setSelectedSubject(defaultSub);
    }
  }, [defaultSub]);

  const [title, setTitle] = useState('');
  const [lessonType, setLessonType] = useState('Text'); // Text, PDF, Video, PPT
  
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]); // Multiple topic IDs for bulk attach
  const [topicSelectionMode, setTopicSelectionMode] = useState('single'); // 'single' | 'multiple'
  const [isTopicDropdownOpen, setIsTopicDropdownOpen] = useState(false);
  const topicDropdownRef = useRef(null);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);

  // Close topic dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (topicDropdownRef.current && !topicDropdownRef.current.contains(event.target)) {
        setIsTopicDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Management tab & edits state
  const [activeTab, setActiveTab] = useState('create'); // 'create' | 'manage'
  const [myLessons, setMyLessons] = useState([]);
  const [myTopics, setMyTopics] = useState([]);
  const [isLoadingManage, setIsLoadingManage] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editTopicTitle, setEditTopicTitle] = useState('');
  const [isDocImporterOpen, setIsDocImporterOpen] = useState(false);
  const [isTopicSplitterOpen, setIsTopicSplitterOpen] = useState(false);

  // Fetch topics from curriculum service (curriculum_topics + syllabus) AND content_approvals
  const fetchTopics = async () => {
    if (!selectedSubject) {
      setTopics([]);
      setSelectedTopic('');
      setSelectedTopics([]);
      return;
    }
    setIsLoadingTopics(true);
    try {
      const cls = getSubjectClass(selectedSubject);
      const subName = getSubjectName(selectedSubject);

      // 1. Fetch from comprehensive curriculum service
      let curriculumList = [];
      try {
        curriculumList = await fetchCurriculumTopics(cls, subName);
      } catch (e) {
        console.warn("curriculum fetch error:", e);
      }

      // 2. Fetch approved teacher-created topics & topics from lessons in content_approvals
      const approvalsList = [];
      try {
        const snap = await getDocs(collection(db, 'content_approvals'));
        snap.docs.forEach((d) => {
          const data = d.data();
          const dSubj = (data.subjectId || data.subjectName || data.subject || '').toLowerCase().trim();
          const targetSubj = (selectedSubject || '').toLowerCase().trim();
          const targetSubjName = (subName || '').toLowerCase().trim();

          const subjMatch = !dSubj || 
            dSubj === targetSubj || 
            dSubj === targetSubjName || 
            dSubj.includes(targetSubj) || 
            targetSubj.includes(dSubj);

          const dClass = (data.classLevel || data.class || '').toLowerCase().trim();
          const targetClass = (selectedClass || '').toLowerCase().trim();
          const classMatch = !dClass || dClass === targetClass || dClass.includes(targetClass);

          if (subjMatch && classMatch) {
            // Explicit Topic document
            if (data.type === 'Topic' || data.type === 'topic') {
              const explicitTitle = (data.title || data.name || '').trim();
              if (explicitTitle) {
                approvalsList.push({
                  ...data,
                  id: d.id,
                  title: explicitTitle,
                  term: data.term || 'Term 1',
                  order: data.order !== undefined ? data.order : 99,
                });
              }
            } else {
              // Extract parent topic name from Lesson / Content
              const parentTopic = (
                data.topicName ||
                data.topicTitle ||
                data.contentData?.topicName ||
                data.contentData?.topicTitle ||
                ''
              ).trim();

              if (parentTopic) {
                const topicId = data.topicId || data.contentData?.topicId || `topic_${parentTopic.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
                approvalsList.push({
                  id: topicId,
                  topicId: topicId,
                  title: parentTopic,
                  term: data.term || data.contentData?.term || term || 'Term 1',
                  order: 99,
                  source: 'content_approvals_lesson',
                });
              }
            }
          }
        });
      } catch (err) {
        console.warn("content_approvals read error in fetchTopics:", err);
      }

      // 3. Merge without duplicate titles
      const combined = [];
      const seenTitles = new Set();

      const addTopicIfUnique = (item) => {
        const rawTitle = item.title || item.name || '';
        const cleanT = rawTitle.toLowerCase().trim();
        if (!cleanT || seenTitles.has(cleanT)) return;
        seenTitles.add(cleanT);
        combined.push({
          id: item.id || item.topicId,
          title: rawTitle,
          term: item.term || 'Term 1',
          order: item.order !== undefined ? item.order : 99,
          ...item,
        });
      };

      if (Array.isArray(curriculumList)) {
        curriculumList.forEach(addTopicIfUnique);
      }
      approvalsList.forEach(addTopicIfUnique);

      combined.sort((a, b) => (a.order || 0) - (b.order || 0));
      setTopics(combined);
      if (!editingLessonId && combined.length > 0) {
        setSelectedTopic(prev => combined.some(t => t.id === prev) ? prev : (combined[0]?.id || ''));
        setSelectedTopics(prev => prev.length > 0 && prev.every(id => combined.some(t => t.id === id)) ? prev : [combined[0]?.id || '']);
      }
    } catch (err) {
      console.error("Error fetching topics:", err);
    } finally {
      setIsLoadingTopics(false);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, [selectedSubject, selectedClass]);

  // Filter topics by Term (if topics have term specified, otherwise show all)
  const filteredTopics = useMemo(() => {
    if (!term || topics.length === 0) return topics;
    const termMatches = topics.filter(t => !t.term || t.term.toLowerCase() === term.toLowerCase());
    return termMatches.length > 0 ? termMatches : topics;
  }, [topics, term]);

  // Topic search query
  const [topicSearchQuery, setTopicSearchQuery] = useState('');

  // Cleaned, filtered topics excluding calendar notices and matching search query
  const displayTopics = useMemo(() => {
    return filteredTopics.filter(t => {
      if (isCalendarNotice(t.title)) return false;
      if (!topicSearchQuery.trim()) return true;
      const q = topicSearchQuery.toLowerCase();
      const cleaned = cleanTopicTitle(t.title).toLowerCase();
      return cleaned.includes(q);
    });
  }, [filteredTopics, topicSearchQuery]);

  const selectedTopicObject = useMemo(() => {
    return displayTopics.find(t => t.id === selectedTopic) || null;
  }, [displayTopics, selectedTopic]);

  const selectedTopicIndex = useMemo(() => {
    return displayTopics.findIndex(t => t.id === selectedTopic);
  }, [displayTopics, selectedTopic]);


  // Keep selectedTopic and selectedTopics valid as displayTopics changes
  useEffect(() => {
    if (!editingLessonId && displayTopics.length > 0) {
      if (!displayTopics.some(t => t.id === selectedTopic)) {
        const firstId = displayTopics[0]?.id || '';
        setSelectedTopic(firstId);
        setSelectedTopics(prev => prev.length > 0 && prev.some(id => displayTopics.some(t => t.id === id)) ? prev : [firstId]);
      }
    }
  }, [displayTopics, editingLessonId]);

  // Topic selection helpers
  const handleTopicClick = (topicId) => {
    if (topicSelectionMode === 'single') {
      setSelectedTopic(topicId);
      setSelectedTopics([topicId]);
      setIsTopicDropdownOpen(false);
    } else {
      setSelectedTopics(prev => {
        const exists = prev.includes(topicId);
        const next = exists ? prev.filter(id => id !== topicId) : [...prev, topicId];
        if (next.length > 0) {
          setSelectedTopic(next[0]);
        } else {
          setSelectedTopic('');
        }
        return next;
      });
    }
  };

  const handleSelectAllTopics = () => {
    const allIds = displayTopics.map(t => t.id);
    setSelectedTopics(allIds);
    if (allIds.length > 0) setSelectedTopic(allIds[0]);
  };

  const handleClearAllTopics = () => {
    setSelectedTopics([]);
    setSelectedTopic('');
  };

  // Filter & Search states for Manage Topics
  const [manageTopicSearch, setManageTopicSearch] = useState('');
  const [manageClassFilter, setManageClassFilter] = useState('ALL');

  const fetchMyContent = async () => {
    if (!userProfile) return;
    setIsLoadingManage(true);
    try {
      // 1. Fetch lessons from content_approvals
      const q = isAdmin
        ? query(collection(db, 'content_approvals'))
        : query(
            collection(db, 'content_approvals'),
            where('teacherId', '==', userProfile.uid)
          );
      const snap = await getDocs(q);
      const docs = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const lessonsList = docs.filter(d => d.type === 'Lesson');
      lessonsList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setMyLessons(lessonsList);

      // 2. Fetch all curriculum topics from curriculum_topics, syllabus, and content_approvals
      const allTopics = [];
      const seenTopicKeys = new Set();

      // (A) From curriculum_topics collection (created in Curriculum Manager)
      try {
        const snapCurriculum = await getDocs(collection(db, 'curriculum_topics'));
        snapCurriculum.docs.forEach((d) => {
          const data = d.data();
          const rawTitle = (data.title || data.name || '').trim();
          if (!rawTitle || isSubjectTitle(rawTitle)) return;

          const cls = (data.classId || data.class || data.classLevel || 'ss1').toLowerCase().trim();
          const subj = (data.subjectId || data.subject || 'mathematics').toLowerCase().trim();
          const normKey = `${cls}_${subj}_${rawTitle.toLowerCase()}`;

          if (!seenTopicKeys.has(normKey)) {
            seenTopicKeys.add(normKey);
            allTopics.push({
              id: d.id,
              topicId: data.topicId || d.id,
              docId: d.id,
              title: rawTitle,
              classId: cls,
              subjectId: subj,
              term: data.term || 'Term 1',
              order: data.order !== undefined && data.order !== null ? Number(data.order) : 1,
              stepsCount: data.stepsCount || (data.steps ? data.steps.length : 0),
              source: 'curriculum',
              sourceLabel: 'Curriculum',
              ...data,
            });
          }
        });
      } catch (err) {
        console.warn('Failed to load curriculum_topics in Manage:', err?.message);
      }

      // (B) From syllabus collection (only real subtopics or explicit type === 'topic')
      try {
        const snapSyllabus = await getDocs(collection(db, 'syllabus'));
        snapSyllabus.docs.forEach((d) => {
          const data = d.data();
          const cls = (data.class || data.classLevel || 'ss1').toLowerCase().trim();
          const subj = (data.subject || data.subjectId || 'mathematics').toLowerCase().trim();

          if (Array.isArray(data.topics)) {
            data.topics.forEach((subT, idx) => {
              const rawTitle = typeof subT === 'string' ? subT.trim() : (subT?.title || subT?.name || '').trim();
              if (!rawTitle || isSubjectTitle(rawTitle)) return;
              const normKey = `${cls}_${subj}_${rawTitle.toLowerCase()}`;
              if (!seenTopicKeys.has(normKey)) {
                seenTopicKeys.add(normKey);
                allTopics.push({
                  id: (typeof subT === 'object' && subT?.id) ? subT.id : `${d.id}_t_${idx}`,
                  topicId: (typeof subT === 'object' && subT?.id) ? subT.id : `${d.id}_t_${idx}`,
                  docId: d.id,
                  title: rawTitle,
                  classId: cls,
                  subjectId: subj,
                  term: (typeof subT === 'object' && subT?.term) || 'Term 1',
                  order: (typeof subT === 'object' && subT?.order !== undefined) ? Number(subT.order) : idx + 1,
                  stepsCount: (typeof subT === 'object' && subT?.steps?.length) || 3,
                  source: 'syllabus_collection',
                  sourceLabel: 'Syllabus',
                  isSubTopicArray: true,
                  subTopicIndex: idx,
                });
              }
            });
          } else if (data.type === 'topic') {
            const rawTitle = (data.title || data.name || '').trim();
            if (rawTitle && !isSubjectTitle(rawTitle)) {
              const normKey = `${cls}_${subj}_${rawTitle.toLowerCase()}`;
              if (!seenTopicKeys.has(normKey)) {
                seenTopicKeys.add(normKey);
                allTopics.push({
                  id: d.id,
                  topicId: d.id,
                  docId: d.id,
                  title: rawTitle,
                  classId: cls,
                  subjectId: subj,
                  term: data.term || 'Term 1',
                  order: data.order !== undefined ? Number(data.order) : 1,
                  stepsCount: data.stepsCount || (data.steps ? data.steps.length : 3),
                  source: 'syllabus_collection',
                  sourceLabel: 'Syllabus',
                  ...data,
                });
              }
            }
          }
        });
      } catch (err) {
        console.warn('Failed to load syllabus topics in Manage:', err?.message);
      }

      // (C) From content_approvals (custom teacher topics)
      const customTopics = docs.filter(d => d.type === 'Topic' || d.type === 'topic');
      customTopics.forEach((t) => {
        const rawTitle = (t.title || t.name || '').trim();
        if (!rawTitle || isSubjectTitle(rawTitle)) return;
        const cls = (t.classLevel || t.class || 'ss1').toLowerCase().trim();
        const subj = (t.subjectId || t.subject || 'mathematics').toLowerCase().trim();
        const normKey = `${cls}_${subj}_${rawTitle.toLowerCase()}`;

        if (!seenTopicKeys.has(normKey)) {
          seenTopicKeys.add(normKey);
          allTopics.push({
            id: t.id,
            topicId: t.topicId || t.id,
            docId: t.id,
            title: rawTitle,
            classId: cls,
            subjectId: subj,
            term: t.term || 'Term 1',
            order: t.order !== undefined ? Number(t.order) : 99,
            source: 'content_approvals',
            sourceLabel: 'Custom',
            ...t,
          });
        }
      });

      // Filter by teacher assigned subjects if not admin
      let displayableTopics = allTopics;
      if (!isAdmin && userProfile?.assignedSubjects?.length > 0) {
        const assignedSet = new Set(userProfile.assignedSubjects.map(s => s.toLowerCase()));
        displayableTopics = allTopics.filter(t => {
          const tSubjId = `${t.subjectId}_${t.classId}`.toLowerCase();
          return assignedSet.has(tSubjId) || assignedSet.has(t.subjectId.toLowerCase());
        });
      }

      // Sort by class, subject, term, and order
      displayableTopics.sort((a, b) => {
        const classComp = (a.classId || '').localeCompare(b.classId || '');
        if (classComp !== 0) return classComp;
        const subjComp = (a.subjectId || '').localeCompare(b.subjectId || '');
        if (subjComp !== 0) return subjComp;
        const termComp = (a.term || '').localeCompare(b.term || '');
        if (termComp !== 0) return termComp;
        return (a.order || 0) - (b.order || 0);
      });

      setMyTopics(displayableTopics);
    } catch (err) {
      console.error("Error fetching my content:", err);
    } finally {
      setIsLoadingManage(false);
    }
  };

  useEffect(() => {
    fetchMyContent();
  }, [userProfile]);

  // Filtered topics for the Manage tab view
  const filteredManageTopics = useMemo(() => {
    return myTopics.filter(t => {
      if (manageClassFilter !== 'ALL') {
        const cls = (t.classId || t.classLevel || getSubjectClass(t.subjectId) || '').toUpperCase();
        if (cls !== manageClassFilter) return false;
      }
      if (manageTopicSearch.trim()) {
        const q = manageTopicSearch.toLowerCase();
        const titleMatch = (t.title || '').toLowerCase().includes(q);
        const subjName = getSubjectName(t.subjectId) || t.subjectId || '';
        const subjMatch = subjName.toLowerCase().includes(q);
        const termMatch = (t.term || '').toLowerCase().includes(q);
        if (!titleMatch && !subjMatch && !termMatch) return false;
      }
      return true;
    });
  }, [myTopics, manageClassFilter, manageTopicSearch]);

  // View toggle for My Lessons and Manage Topics (Default 1-3, with "View All" toggle)
  const [showAllLessons, setShowAllLessons] = useState(false);
  const displayedLessons = useMemo(() => {
    return showAllLessons ? myLessons : myLessons.slice(0, 3);
  }, [myLessons, showAllLessons]);

  const [showAllTopics, setShowAllTopics] = useState(false);
  const displayedManageTopics = useMemo(() => {
    return showAllTopics ? filteredManageTopics : filteredManageTopics.slice(0, 3);
  }, [filteredManageTopics, showAllTopics]);

  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);

  const handleCreateTopic = async () => {
    if (!newTopicTitle.trim()) {
      setErrorMsg("Please enter a topic title.");
      return;
    }
    if (!selectedSubject) {
      setErrorMsg("Please select a subject first.");
      return;
    }
    setIsCreatingTopic(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const docRef = doc(collection(db, 'content_approvals'));
      const trimmedTitle = newTopicTitle.trim();
      await setDoc(docRef, {
        id: docRef.id,
        title: trimmedTitle,
        type: 'Topic',
        subjectId: selectedSubject,
        classLevel: selectedClass,
        term: term,
        status: 'approved',
        order: topics.length + 1,
        teacherId: userProfile?.uid || 'teacher',
        teacherName: userProfile?.fullName || 'Teacher',
        createdAt: serverTimestamp(),
      });

      await fetchTopics();
      setSelectedTopic(docRef.id);
      setSelectedTopics(prev => prev.includes(docRef.id) ? prev : [...prev, docRef.id]);
      setNewTopicTitle('');
      setSuccessMsg(`Curriculum topic "${trimmedTitle}" created and selected!`);
      fetchMyContent();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || "Failed to create curriculum topic.");
    } finally {
      setIsCreatingTopic(false);
    }
  };
  
  // Content values
  const [content, setContent] = useState('');
  const [subtopic, setSubtopic] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [externalLink, setExternalLink] = useState('');
  
  // File upload state & AI text extraction
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [isExtractingText, setIsExtractingText] = useState(false);
  const [extractionStatus, setExtractionStatus] = useState('');
  
  // Lesson Visual Diagram state (Cloudinary upload or URL)
  const [diagramUrl, setDiagramUrl] = useState('');
  const [diagramCaption, setDiagramCaption] = useState('');
  const [diagramSpoken, setDiagramSpoken] = useState('');
  const [isUploadingDiagram, setIsUploadingDiagram] = useState(false);
  const diagramFileInputRef = useRef(null);

  // Lesson Table state (comparison table, data chart, or hints for AI)
  const [tableTitle, setTableTitle] = useState('');
  const [tableContent, setTableContent] = useState('');
  const [tableSpoken, setTableSpoken] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleDiagramFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMsg("Please select a valid image file (.png, .jpg, .jpeg, .webp, .gif) for the lesson diagram.");
      return;
    }

    setIsUploadingDiagram(true);
    setErrorMsg('');
    try {
      const secureUrl = await uploadLibraryFile(file, 'smartbrain/diagrams');
      if (secureUrl) {
        setDiagramUrl(secureUrl);
        if (!diagramCaption) {
          const baseName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
          setDiagramCaption(`Figure: ${baseName.charAt(0).toUpperCase() + baseName.slice(1)}`);
        }
      }
    } catch (err) {
      console.error('Cloudinary diagram upload error:', err);
      setErrorMsg('Failed to upload diagram image. You can also paste an image URL directly.');
    } finally {
      setIsUploadingDiagram(false);
    }
  };

  const handleClearDiagram = () => {
    setDiagramUrl('');
    setDiagramCaption('');
    setDiagramSpoken('');
    if (diagramFileInputRef.current) diagramFileInputRef.current.value = '';
  };

  const handleClearTable = () => {
    setTableTitle('');
    setTableContent('');
    setTableSpoken('');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate extensions (support pdf, txt, text, md, ppt, pptx, mp4, webm, ogg)
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'txt', 'text', 'md', 'ppt', 'pptx', 'mp4', 'webm', 'ogg'].includes(ext)) {
      setErrorMsg("Please select a valid file (.pdf, .txt, .ppt, .pptx, .mp4, .webm, .ogg).");
      return;
    }

    setSelectedFile(file);
    setErrorMsg('');

    // If it's a readable document (PDF or Text), extract full text for AI Teacher retrieval
    if (['pdf', 'txt', 'text', 'md'].includes(ext)) {
      setIsExtractingText(true);
      setExtractionStatus('Extracting lesson text for AI Teacher...');
      try {
        let textResult = '';
        if (ext === 'pdf') {
          const res = await extractDocumentText({ file });
          textResult = res?.text || '';
        } else {
          textResult = await file.text();
        }

        if (textResult && textResult.trim()) {
          const cleanedText = textResult.trim();
          setExtractedText(cleanedText);
          // If teacher hasn't typed content yet, populate in content textarea
          if (!content.trim()) {
            setContent(cleanedText);
          }
          const wordCount = cleanedText.split(/\s+/).filter(Boolean).length;
          setExtractionStatus(`Extracted ${wordCount.toLocaleString()} words from document for AI Teacher retrieval.`);
        } else {
          setExtractionStatus('Document attached. (No readable text layer found)');
        }
      } catch (err) {
        console.warn('PDF/Doc extraction notice:', err);
        setExtractionStatus('Document attached. (Automatic text parsing skipped or unavailable)');
      } finally {
        setIsExtractingText(false);
      }
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setUploadProgress(null);
    setExtractedText('');
    setExtractionStatus('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSubject || !title.trim()) {
      setErrorMsg("Subject and Title are required.");
      return;
    }

    const targetTopicIds = (topicSelectionMode === 'multiple' && selectedTopics.length > 0)
      ? selectedTopics
      : (selectedTopic ? [selectedTopic] : []);

    if (targetTopicIds.length === 0) {
      setErrorMsg("Please select at least one curriculum topic.");
      return;
    }
    
    if (!content.trim() && !selectedFile && !externalLink.trim()) {
      setErrorMsg("Please provide either Lesson Text Content or a File Upload.");
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      let uploadedUrl = null;

      if (selectedFile && !selectedFile.isExisting) {
        uploadedUrl = await uploadLibraryFile(selectedFile, 'teacher_uploads', (progress) => {
          setUploadProgress(progress);
        });
      } else if (selectedFile?.isExisting) {
        const existingLesson = myLessons.find(l => l.id === editingLessonId);
        uploadedUrl = existingLesson?.contentData?.mediaUrl || null;
      }

      const lessonStatus = isAdmin ? 'approved' : 'pending';
      const isPdf = selectedFile?.name?.toLowerCase().endsWith('.pdf');

      let currentExtracted = extractedText.trim();
      // Automatic safety net: if a PDF was attached and extraction is still pending or wasn't triggered, extract now!
      if (selectedFile && isPdf && !currentExtracted && !content.trim()) {
        try {
          const res = await extractDocumentText({ file: selectedFile });
          if (res?.text) {
            currentExtracted = res.text.trim();
          }
        } catch (e) {
          console.warn('PDF extraction fallback on submit failed:', e);
        }
      }

      const finalBody = content.trim() || currentExtracted;
      const chunks = chunkLessonContent(currentExtracted || content.trim());

      if (editingLessonId) {
        const primaryTopicId = targetTopicIds[0];
        const chosenTopic = topics.find(t => t.id === primaryTopicId);
        const lessonData = {
          lessonType: selectedFile ? (isPdf ? 'PDF' : 'Mixed') : 'Mixed',
          title: title.trim(),
          term: term,
          topicId: primaryTopicId,
          topicName: chosenTopic?.title || '',
          topicIds: targetTopicIds,
          subtopic: subtopic.trim(),
          subtopics: subtopic.trim() ? [subtopic.trim()] : [],
          ...(finalBody && { body: finalBody }),
          ...(extractedText.trim() && { extractedText: extractedText.trim() }),
          ...(chunks.length > 0 && { textChunks: chunks }),
          ...(uploadedUrl && { mediaUrl: uploadedUrl }),
          ...(externalLink.trim() && { externalLink: externalLink.trim() }),
          ...(selectedFile && { fileName: selectedFile.name, fileSize: selectedFile.size }),
          ...(diagramUrl.trim() && { diagramUrl: diagramUrl.trim() }),
          ...(diagramCaption.trim() && { diagramCaption: diagramCaption.trim() }),
          ...(diagramSpoken.trim() && { diagramSpoken: diagramSpoken.trim() }),
          ...(tableTitle.trim() && { tableTitle: tableTitle.trim() }),
          ...(tableContent.trim() && { tableContent: tableContent.trim() }),
          ...(tableSpoken.trim() && { tableSpoken: tableSpoken.trim() }),
          isAIExtracted: Boolean(extractedText.trim()),
          updatedAt: serverTimestamp(),
        };

        const docRef = doc(db, 'content_approvals', editingLessonId);
        await setDoc(docRef, {
          title: title.trim(),
          subjectId: selectedSubject,
          subjectName: getSubjectName(selectedSubject),
          classLevel: getSubjectClass(selectedSubject),
          status: lessonStatus,
          contentData: lessonData,
          updatedAt: serverTimestamp(),
        }, { merge: true });

        setSuccessMsg(isAdmin ? "Lesson updated and published successfully!" : "Lesson updated successfully and submitted for admin approval!");
        setEditingLessonId(null);
      } else {
        // Multi-topic or single-topic creation: create a record for each topic
        const chosenTopics = topics.filter(t => targetTopicIds.includes(t.id));
        for (const topicId of targetTopicIds) {
          const tObj = chosenTopics.find(t => t.id === topicId);
          const tName = tObj?.title || '';
          const singleLessonData = {
            lessonType: selectedFile ? (isPdf ? 'PDF' : 'Mixed') : 'Mixed',
            title: title.trim(),
            term: term,
            topicId: topicId,
            topicName: tName,
            topicIds: targetTopicIds,
            subtopic: subtopic.trim(),
            subtopics: subtopic.trim() ? [subtopic.trim()] : [],
            ...(finalBody && { body: finalBody }),
            ...(extractedText.trim() && { extractedText: extractedText.trim() }),
            ...(chunks.length > 0 && { textChunks: chunks }),
            ...(uploadedUrl && { mediaUrl: uploadedUrl }),
            ...(externalLink.trim() && { externalLink: externalLink.trim() }),
            ...(selectedFile && { fileName: selectedFile.name, fileSize: selectedFile.size }),
            ...(diagramUrl.trim() && { diagramUrl: diagramUrl.trim() }),
            ...(diagramCaption.trim() && { diagramCaption: diagramCaption.trim() }),
            ...(diagramSpoken.trim() && { diagramSpoken: diagramSpoken.trim() }),
            ...(tableTitle.trim() && { tableTitle: tableTitle.trim() }),
            ...(tableContent.trim() && { tableContent: tableContent.trim() }),
            ...(tableSpoken.trim() && { tableSpoken: tableSpoken.trim() }),
            isAIExtracted: Boolean(extractedText.trim()),
            createdAt: serverTimestamp(),
          };

          const docRef = doc(collection(db, 'content_approvals'));
          await setDoc(docRef, {
            id: docRef.id,
            title: title.trim(),
            type: 'Lesson',
            subjectId: selectedSubject,
            subjectName: getSubjectName(selectedSubject),
            classLevel: getSubjectClass(selectedSubject),
            teacherId: userProfile.uid,
            teacherName: userProfile.fullName || (isAdmin ? 'Admin' : 'Teacher'),
            status: lessonStatus,
            createdAt: serverTimestamp(),
            contentData: singleLessonData,
          });
        }

        const countText = targetTopicIds.length > 1 ? ` for ${targetTopicIds.length} topics` : '';
        setSuccessMsg(isAdmin ? `Lesson published${countText} successfully!` : `Lesson submitted${countText} for admin approval!`);
      }
      
      // Reset form
      setTitle('');
      setSubtopic('');
      setContent('');
      setExtractedText('');
      setExtractionStatus('');
      setVideoUrl('');
      setExternalLink('');
      setTerm('Term 1');
      clearFile();
      handleClearDiagram();
      handleClearTable();

      // Refresh data
      fetchMyContent();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || "Failed to save lesson. Please try again.");
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("Are you sure you want to delete this lesson? This action cannot be undone.")) return;
    try {
      setErrorMsg('');
      setSuccessMsg('');
      await deleteDoc(doc(db, 'content_approvals', lessonId));
      setSuccessMsg("Lesson deleted successfully!");
      fetchMyContent();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || "Failed to delete lesson.");
    }
  };

  const handleDeleteTopic = async (topicOrId) => {
    const topicObj = typeof topicOrId === 'object' && topicOrId !== null
      ? topicOrId 
      : (myTopics.find(t => t.id === topicOrId || t.topicId === topicOrId) || { id: topicOrId });
    const topicName = topicObj.title || 'this curriculum topic';

    if (!window.confirm(`Are you sure you want to delete "${topicName}"? This action will remove it from the curriculum.`)) return;
    try {
      setErrorMsg('');
      setSuccessMsg('');
      await deleteCurriculumTopic(topicObj);
      setSuccessMsg(`Curriculum topic "${topicName}" deleted successfully!`);
      await fetchMyContent();
      await fetchTopics();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || "Failed to delete curriculum topic.");
    }
  };

  const handleEditLesson = (lesson) => {
    setEditingLessonId(lesson.id);
    setSelectedSubject(lesson.subjectId);
    setTitle(lesson.title);
    
    const contentData = lesson.contentData || {};
    setLessonType(contentData.lessonType || 'Mixed');
    const primaryId = contentData.topicId || '';
    const ids = (Array.isArray(contentData.topicIds) && contentData.topicIds.length > 0)
      ? contentData.topicIds
      : (primaryId ? [primaryId] : []);
    setSelectedTopic(primaryId);
    setSelectedTopics(ids);
    setTopicSelectionMode(ids.length > 1 ? 'multiple' : 'single');
    setTerm(contentData.term || 'Term 1');
    setSubtopic(contentData.subtopic || (Array.isArray(contentData.subtopics) ? contentData.subtopics[0] : '') || '');
    
    setContent(contentData.body || '');
    setExtractedText(contentData.extractedText || '');
    setExtractionStatus(contentData.extractedText ? 'Extracted text loaded from existing lesson.' : '');
    setExternalLink(contentData.externalLink || contentData.mediaUrl || '');
    
    if (contentData.fileName) {
      setSelectedFile({ name: contentData.fileName, size: contentData.fileSize || 0, isExisting: true });
    } else {
      setSelectedFile(null);
    }

    setDiagramUrl(contentData.diagramUrl || '');
    setDiagramCaption(contentData.diagramCaption || '');
    setDiagramSpoken(contentData.diagramSpoken || '');
    setTableTitle(contentData.tableTitle || '');
    setTableContent(contentData.tableContent || '');
    setTableSpoken(contentData.tableSpoken || '');
    
    setActiveTab('create');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateTopic = async (topicId) => {
    if (!editTopicTitle.trim()) return;
    try {
      setErrorMsg('');
      setSuccessMsg('');
      const target = myTopics.find(t => t.id === topicId || t.topicId === topicId);
      if (target) {
        await saveCurriculumTopic({
          ...target,
          title: editTopicTitle.trim(),
        });
      } else {
        await setDoc(doc(db, 'content_approvals', topicId), {
          title: editTopicTitle.trim()
        }, { merge: true });
      }
      
      setSuccessMsg("Curriculum topic updated successfully!");
      setEditingTopicId(null);
      await fetchMyContent();
      await fetchTopics();
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to update topic.");
    }
  };

  const handleCancelEdit = () => {
    setEditingLessonId(null);
    setTitle('');
    setSubtopic('');
    setContent('');
    setExtractedText('');
    setExtractionStatus('');
    setVideoUrl('');
    setExternalLink('');
    setTerm('Term 1');
    clearFile();
    handleClearDiagram();
    handleClearTable();
  };

  const formatFileSize = (bytes) => {
    if (bytes <= 0) return '0 B';
    const suffixes = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < suffixes.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${suffixes[i]}`;
  };

  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-darkNavy mb-2 font-poppins">Upload & Manage Content</h1>
        <p className="text-sm md:text-base text-gray-500">Publish or modify your curriculum topics and lesson materials.</p>
      </div>

      {/* Tab Switcher (Non-white, colorful themed pill buttons) */}
      <div className="flex items-center gap-3 mb-8">
        <button
          type="button"
          onClick={() => {
            setActiveTab('create');
            setErrorMsg('');
            setSuccessMsg('');
          }}
          className={`py-2.5 px-6 font-bold font-poppins text-sm rounded-xl border transition-all cursor-pointer ${
            activeTab === 'create'
              ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
              : 'bg-blue-100 text-blue-900 border-blue-300 hover:bg-blue-200'
          }`}
        >
          {editingLessonId ? 'Edit Lesson' : 'Upload Lesson'}
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveTab('manage');
            setErrorMsg('');
            setSuccessMsg('');
            fetchMyContent();
          }}
          className={`py-2.5 px-6 font-bold font-poppins text-sm rounded-xl border transition-all cursor-pointer ${
            activeTab === 'manage'
              ? 'bg-purple-600 text-white border-purple-700 shadow-sm'
              : 'bg-purple-100 text-purple-900 border-purple-300 hover:bg-purple-200'
          }`}
        >
          Manage
        </button>
      </div>

      {successMsg && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-xl flex items-center gap-3 border border-green-200">
          <MdCheckCircle size={24} />
          <span className="font-semibold">{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200">
          <span className="font-semibold">{errorMsg}</span>
        </div>
      )}

      {activeTab === 'create' ? (
        <form onSubmit={handleSubmit} className="bg-slate-50/70 p-4 sm:p-6 md:p-8 rounded-2xl border border-slate-200/90 shadow-sm">
          {editingLessonId && (
            <div className="mb-6 p-4 bg-blue-50 text-blue-800 rounded-xl flex items-center justify-between border border-blue-200">
              <span className="text-sm font-semibold">Editing Lesson: <strong className="text-blue-900">{title}</strong> (Updates will revert status to pending)</span>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-sm text-red-600 hover:text-red-800 font-bold flex items-center gap-1"
              >
                <MdCancel size={16} /> Cancel Edit
              </button>
            </div>
          )}

          {/* 3-Step Cascading Selector: Class -> Subject -> Term */}
          <div className="mb-6 p-5 bg-gradient-to-r from-blue-50/70 to-indigo-50/50 rounded-2xl border border-blue-100 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 1. Class Level */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  1. Class Level
                </label>
                <select 
                  value={selectedClass}
                  onChange={(e) => handleClassChange(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl font-semibold text-darkNavy shadow-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all cursor-pointer"
                  required
                >
                  {availableClasses.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* 2. Subject (Filtered specifically to this class) */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  2. Subject
                </label>
                <select 
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl font-semibold text-darkNavy shadow-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all cursor-pointer"
                  required
                >
                  {availableSubjectsForClass.length === 0 && <option value="">No subjects in {selectedClass}</option>}
                  {availableSubjectsForClass.map(sub => (
                    <option key={sub} value={sub}>{getSubjectName(sub)}</option>
                  ))}
                </select>
              </div>

              {/* 3. Term */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  3. Academic Term
                </label>
                <select
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl font-semibold text-darkNavy shadow-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all cursor-pointer"
                  required
                >
                  <option value="Term 1">Term 1</option>
                  <option value="Term 2">Term 2</option>
                  <option value="Term 3">Term 3</option>
                </select>
              </div>
            </div>
          </div>

          {/* 4. Curriculum Topic Dropdown Selector */}
          <div className="mb-6 relative z-30" ref={topicDropdownRef}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                  4. Curriculum Topic{topicSelectionMode === 'multiple' ? 's' : ''}
                </label>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  Select from {displayTopics.length} topics created in your {selectedClass} • {term} syllabus
                </p>
              </div>

              {/* Mode Toggle: Single vs Multiple */}
              <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
                <div className="flex items-center bg-gray-100 p-0.5 rounded-xl border border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setTopicSelectionMode('single');
                      if (selectedTopics.length > 0) setSelectedTopic(selectedTopics[0]);
                    }}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      topicSelectionMode === 'single'
                        ? 'bg-white text-primary shadow-xs'
                        : 'text-gray-600 hover:text-darkNavy'
                    }`}
                  >
                    Single Topic
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTopicSelectionMode('multiple');
                      if (selectedTopic && !selectedTopics.includes(selectedTopic)) {
                        setSelectedTopics([selectedTopic]);
                      }
                    }}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      topicSelectionMode === 'multiple'
                        ? 'bg-primary text-white shadow-xs'
                        : 'text-gray-600 hover:text-darkNavy'
                    }`}
                  >
                    Multiple Topics
                  </button>
                </div>

                <span className="text-xs font-bold text-primary bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-lg">
                  {topicSelectionMode === 'multiple' 
                    ? `${selectedTopics.length} selected`
                    : `${displayTopics.length} available`}
                </span>
              </div>
            </div>

            {/* Hidden field for form validation */}
            <input 
              type="hidden" 
              value={topicSelectionMode === 'multiple' ? (selectedTopics.length > 0 ? selectedTopics.join(',') : '') : selectedTopic} 
              required 
            />

            {/* Dropdown Selector Trigger Button */}
            {isLoadingTopics ? (
              <div className="w-full px-4 py-3 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-xs font-semibold text-gray-500 flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                Loading curriculum topics for {getSubjectName(selectedSubject)} ({selectedClass})...
              </div>
            ) : (
              <div>
                <button
                  type="button"
                  onClick={() => setIsTopicDropdownOpen(prev => !prev)}
                  className={`w-full px-4 py-3 bg-white border rounded-xl font-semibold text-darkNavy shadow-xs hover:border-primary/60 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none transition-all flex items-center justify-between text-left cursor-pointer ${
                    isTopicDropdownOpen ? 'border-primary ring-2 ring-primary/20' : 'border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1 pr-3">
                    <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {topicSelectionMode === 'single' ? (
                        selectedTopicIndex >= 0 ? selectedTopicIndex + 1 : <MdLibraryBooks size={14} />
                      ) : (
                        selectedTopics.length || <MdLibraryBooks size={14} />
                      )}
                    </span>
                    <div className="min-w-0 flex-1 truncate">
                      {topicSelectionMode === 'single' ? (
                        selectedTopicObject ? (
                          <div className="flex items-center gap-2 truncate">
                            <span className="text-sm font-bold text-darkNavy truncate">
                              {cleanTopicTitle(selectedTopicObject.title)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 font-normal">
                            Click to select a curriculum topic...
                          </span>
                        )
                      ) : (
                        selectedTopics.length > 0 ? (
                          <div className="flex items-center gap-2 truncate">
                            <span className="text-sm font-bold text-darkNavy">
                              {selectedTopics.length} topic{selectedTopics.length > 1 ? 's' : ''} selected
                            </span>
                            <span className="text-xs text-gray-500 truncate hidden sm:inline">
                              — {selectedTopics.map(id => {
                                const t = displayTopics.find(item => item.id === id);
                                return t ? cleanTopicTitle(t.title).slice(0, 30) : '';
                              }).filter(Boolean).slice(0, 2).join(', ')}{selectedTopics.length > 2 ? '...' : ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 font-normal">
                            Click to select curriculum topic(s)...
                          </span>
                        )
                      )}
                    </div>
                  </div>

                  <div className={`flex items-center gap-1.5 flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    isTopicDropdownOpen 
                      ? 'bg-blue-800 text-white shadow-xs' 
                      : 'bg-primary hover:bg-blue-700 text-white shadow-xs'
                  }`}>
                    <span>
                      {isTopicDropdownOpen ? 'Close' : 'Choose topic'}
                    </span>
                    {isTopicDropdownOpen ? (
                      <MdExpandLess className="text-base text-white" />
                    ) : (
                      <MdExpandMore className="text-base text-white" />
                    )}
                  </div>
                </button>

                {/* Dropdown Menu Popup */}
                {isTopicDropdownOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-2xl border border-gray-200 shadow-2xl z-50 p-3.5 space-y-3">
                    {/* Search Bar & Quick Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between pb-2 border-b border-gray-100">
                      <div className="relative flex-1">
                        <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                        <input
                          type="text"
                          value={topicSearchQuery}
                          onChange={(e) => setTopicSearchQuery(e.target.value)}
                          placeholder={`Search ${getSubjectName(selectedSubject)} topics (e.g. Grammar, Algebra)...`}
                          className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white outline-none transition-all"
                          autoFocus
                        />
                      </div>

                      {topicSelectionMode === 'multiple' && displayTopics.length > 0 && (
                        <div className="flex items-center gap-1.5 self-end sm:self-auto">
                          <button
                            type="button"
                            onClick={handleSelectAllTopics}
                            className="text-[11px] font-bold text-primary hover:text-blue-700 bg-blue-50 hover:bg-blue-100/80 px-2.5 py-1.5 rounded-lg border border-blue-200 transition-colors"
                          >
                            Select All ({displayTopics.length})
                          </button>
                          <button
                            type="button"
                            onClick={handleClearAllTopics}
                            className="text-[11px] font-bold text-gray-600 hover:text-red-600 bg-gray-100 hover:bg-red-50 px-2.5 py-1.5 rounded-lg border border-gray-200 transition-colors"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Scrollable Topics List */}
                    {displayTopics.length > 0 ? (
                      <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                        {displayTopics.map((topic, index) => {
                          const isSelected = topicSelectionMode === 'multiple'
                            ? selectedTopics.includes(topic.id)
                            : selectedTopic === topic.id;
                          const { mainTitle, strands } = parseTopicParts(topic.title);

                          return (
                            <div
                              key={topic.id}
                              onClick={() => handleTopicClick(topic.id)}
                              className={`p-2.5 rounded-xl border transition-all cursor-pointer flex items-start justify-between gap-2.5 text-left ${
                                isSelected
                                  ? 'bg-blue-50/90 border-primary shadow-xs ring-1 ring-primary/30'
                                  : 'bg-white border-gray-150 hover:border-blue-300 hover:bg-blue-50/30'
                              }`}
                            >
                              <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                <div className={`w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5 ${
                                  isSelected ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {index + 1}
                                </div>

                                <div className="min-w-0 flex-1">
                                  <h4 className={`text-xs font-bold leading-snug ${
                                    isSelected ? 'text-primary' : 'text-darkNavy'
                                  }`}>
                                    {mainTitle}
                                  </h4>

                                  {strands.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap mt-1">
                                      {strands.map((strand, sIdx) => (
                                        <span 
                                          key={sIdx} 
                                          className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                            isSelected 
                                              ? 'bg-primary/10 text-primary' 
                                              : 'bg-gray-100 text-gray-600'
                                          }`}
                                        >
                                          {strand}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="flex-shrink-0 pt-0.5">
                                <div className={`w-4 h-4 ${topicSelectionMode === 'multiple' ? 'rounded' : 'rounded-full'} border flex items-center justify-center transition-all ${
                                  isSelected ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'
                                }`}>
                                  {isSelected && <MdCheckCircle size={12} />}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-3 bg-amber-50 text-amber-800 rounded-xl border border-amber-200 text-xs font-medium">
                        {topicSearchQuery ? (
                          <span>No topics match &quot;{topicSearchQuery}&quot;. Please clear or adjust your search.</span>
                        ) : (
                          <span>⚠️ No curriculum topics found for {selectedClass} {getSubjectName(selectedSubject)} ({term}). Please create topics in the Curriculum manager.</span>
                        )}
                      </div>
                    )}

                    {/* Done Action (When in Multiple Mode) */}
                    {topicSelectionMode === 'multiple' && (
                      <div className="pt-2.5 border-t border-gray-100 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setIsTopicDropdownOpen(false)}
                          className="px-4 py-1.5 bg-primary hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors shadow-xs"
                        >
                          Done ({selectedTopics.length} selected)
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Topic Pills (When in Multiple Mode) */}
                {topicSelectionMode === 'multiple' && selectedTopics.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {selectedTopics.map(id => {
                      const topic = displayTopics.find(t => t.id === id);
                      if (!topic) return null;
                      const { mainTitle } = parseTopicParts(topic.title);
                      const idx = displayTopics.findIndex(t => t.id === id);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-primary text-xs font-semibold px-2.5 py-1 rounded-lg shadow-2xs"
                        >
                          <span className="w-4 h-4 rounded bg-primary text-white text-[10px] flex items-center justify-center font-bold">
                            {idx >= 0 ? idx + 1 : '•'}
                          </span>
                          <span className="max-w-[220px] truncate">{mainTitle}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTopicClick(id);
                            }}
                            className="text-primary hover:text-red-600 rounded p-0.5 transition-colors"
                            title="Remove topic"
                          >
                            <MdClose size={13} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

              </div>
            )}
          </div>

          {/* Multi-Lesson Splitter Form Card */}
          {selectedTopic && selectedTopicObject && (
            <div className="mb-6 p-4 sm:p-5 bg-white border border-gray-200 rounded-xl shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h4 className="text-sm font-bold text-darkNavy">
                    Have a document with multiple lessons?
                  </h4>
                  <span className="text-[11px] font-bold text-primary bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-md">
                    Lesson Splitter
                  </span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Auto-split and organize individual sub-lessons for{' '}
                  <span className="font-semibold text-darkNavy">
                    &quot;{parseTopicParts(selectedTopicObject.title).mainTitle || cleanTopicTitle(selectedTopicObject.title)}&quot;
                  </span>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsTopicSplitterOpen(true)}
                className="shrink-0 px-4 py-2.5 bg-primary hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-xs rounded-xl shadow-xs transition-colors flex items-center justify-center cursor-pointer"
              >
                Split PDF into Lessons
              </button>
            </div>
          )}

          {/* 5. Lesson Title */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">Lesson Title</label>
            <input 
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Introduction to Algebra & Notation"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
              required
            />
          </div>

          {/* 6. Subtopic (Optional / Curriculum Strand) */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-bold text-gray-700">
                Subtopic / Specific Concept (Optional)
              </label>
              <span className="text-xs text-primary font-semibold bg-blue-50 px-2.5 py-0.5 rounded-md border border-blue-100">
                AI Teacher Grounding
              </span>
            </div>
            <input 
              type="text"
              value={subtopic}
              onChange={(e) => setSubtopic(e.target.value)}
              placeholder="e.g. Conversion from Base 10 to Base 2, or Quadratic Formula derivation"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
            />
            <p className="text-xs text-gray-400 mt-1">
              Associating a specific subtopic allows the AI Teacher to immediately retrieve this exact section when students ask focused questions.
            </p>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-bold text-gray-700 mb-2">Lesson Content (Optional)</label>
            <textarea 
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your full lecture notes here..."
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            ></textarea>
          </div>

          {/* 7. Lesson Diagram & Visual Aid */}
          <div className="mb-8 p-5 bg-white border border-gray-200 rounded-xl space-y-4 shadow-2xs">
            <div>
              <label className="block text-sm font-bold text-gray-800">
                Lesson Diagram & Visual Aid (Optional)
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Attach an exam diagram, scientific illustration, or chart to display to students during this lesson.
              </p>
            </div>

            {/* Hidden Diagram file input */}
            <input
              type="file"
              ref={diagramFileInputRef}
              onChange={handleDiagramFileChange}
              accept="image/*"
              className="hidden"
            />

            {!diagramUrl ? (
              <div
                onClick={() => !isUploadingDiagram && !isSubmitting && diagramFileInputRef.current?.click()}
                className={`w-full p-5 border-2 border-dashed border-gray-300 hover:border-primary/60 rounded-xl flex flex-col items-center justify-center cursor-pointer bg-gray-50/50 hover:bg-gray-50 transition-all ${isUploadingDiagram || isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <div className="w-10 h-10 bg-blue-50 text-primary rounded-full flex items-center justify-center mb-2">
                  <MdCloudUpload size={22} />
                </div>
                <p className="font-bold text-gray-800 text-xs mb-0.5">
                  {isUploadingDiagram ? 'Uploading diagram...' : 'Upload Image from Computer or Phone'}
                </p>
                <p className="text-[11px] text-gray-400">PNG, JPG, JPEG, WebP</p>
              </div>
            ) : (
              <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between gap-3 shadow-2xs">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={diagramUrl}
                    alt="Diagram Preview"
                    className="h-16 w-24 object-contain rounded-lg border bg-white flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-bold text-gray-800 block truncate">
                      {diagramCaption || 'Lesson Diagram'}
                    </span>
                    <span className="text-[11px] text-gray-400 truncate block">
                      Image attached
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClearDiagram}
                  className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-lg transition-colors flex items-center gap-1 flex-shrink-0"
                >
                  <MdClose size={14} /> Remove
                </button>
              </div>
            )}

            {/* Diagram Caption & Voice guidance */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Diagram Title / Caption
                </label>
                <input
                  type="text"
                  placeholder="e.g. Figure 1: Labeled Structure of an Animal Cell"
                  value={diagramCaption}
                  onChange={(e) => setDiagramCaption(e.target.value)}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Spoken Guidance (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Notice the nucleus and mitochondria labeled in the diagram..."
                  value={diagramSpoken}
                  onChange={(e) => setDiagramSpoken(e.target.value)}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Lesson Table */}
          <div className="mb-8 p-4 bg-gray-50/60 border border-gray-200 rounded-2xl">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-bold text-gray-700">
                Attach Lesson Table
              </label>
              {(tableTitle || tableContent || tableSpoken) && (
                <button
                  type="button"
                  onClick={handleClearTable}
                  className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-lg transition-colors flex items-center gap-1"
                >
                  <MdClose size={13} /> Clear
                </button>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Table Title
                </label>
                <input
                  type="text"
                  placeholder="e.g. Differences Between Plant and Animal Cells"
                  value={tableTitle}
                  onChange={(e) => setTableTitle(e.target.value)}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Table Content
                </label>
                <textarea
                  rows={4}
                  placeholder={`| Feature | Plant Cell | Animal Cell |
| Cell Wall | Present | Absent |
| Chloroplast | Present | Absent |`}
                  value={tableContent}
                  onChange={(e) => setTableContent(e.target.value)}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs bg-white font-mono focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Spoken Guidance (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Look at the comparison table on your screen. Notice that animal cells completely lack a cell wall..."
                  value={tableSpoken}
                  onChange={(e) => setTableSpoken(e.target.value)}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none transition-all"
                />
              </div>
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-bold text-gray-700 mb-2">Upload File (Optional)</label>
              
              <input 
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.txt,.text,.md,.ppt,.pptx,.mp4,.webm,.ogg"
                className="hidden"
              />

              {!selectedFile ? (
                <div 
                  onClick={() => !isSubmitting && fileInputRef.current?.click()}
                  className={`w-full p-8 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="w-16 h-16 bg-blue-50 text-primary rounded-full flex items-center justify-center mb-4">
                    <MdCloudUpload size={32} />
                  </div>
                  <h3 className="font-bold text-darkNavy mb-1">Click to browse file</h3>
                  <p className="text-sm text-gray-500">
                    Supports: .pdf (with automatic AI text extraction), .txt, .ppt, .pptx, .mp4, .webm, .ogg
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-full p-4 border border-gray-200 rounded-xl bg-white flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-blue-50 text-blue-500">
                      <MdCloudUpload size={28} />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="font-bold text-darkNavy truncate">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                      
                      {uploadProgress !== null && (
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full transition-all duration-300" 
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-bold text-primary">{Math.round(uploadProgress)}%</span>
                        </div>
                      )}
                    </div>
                    {uploadProgress === null && !isSubmitting && (
                      <button 
                        type="button" 
                        onClick={clearFile}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <MdClose size={24} />
                      </button>
                    )}
                  </div>

                  {isExtractingText && (
                    <div className="flex items-center gap-2.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-3.5 py-2.5 rounded-xl">
                      <span className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin flex-shrink-0"></span>
                      <span>AI is extracting full document text so the AI Teacher can teach from it...</span>
                    </div>
                  )}

                  {extractionStatus && !isExtractingText && (
                    <div className="flex items-center gap-2.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3.5 py-2.5 rounded-xl">
                      <MdCheckCircle className="text-emerald-600 text-base flex-shrink-0" />
                      <span>{extractionStatus}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

          <button 
            type="submit"
            disabled={isSubmitting || isExtractingText || subjects.length === 0}
            className="w-full py-4 bg-primary text-white font-bold rounded-xl shadow-md hover:bg-blue-700 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : isExtractingText ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Extracting Text from Document...</span>
              </>
            ) : (
              <>
                <MdLibraryBooks size={20} /> {editingLessonId ? 'Update Lesson' : 'Submit Lesson for Approval'}
              </>
            )}
          </button>
        </form>
      ) : (
        <div className="space-y-6 md:space-y-8 animate-fadeIn">
          {/* Lessons Section */}
          <div className="bg-white p-4 md:p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-darkNavy font-poppins flex items-center gap-2">
                  <MdLibraryBooks className="text-primary" /> My Lessons ({myLessons.length})
                </h2>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  {myLessons.length > 3 
                    ? `Showing ${displayedLessons.length} of ${myLessons.length} lessons`
                    : 'All uploaded lessons'}
                </p>
              </div>

              {myLessons.length > 3 && (
                <button
                  type="button"
                  onClick={() => setShowAllLessons(prev => !prev)}
                  className="px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border shadow-2xs self-start sm:self-auto cursor-pointer bg-blue-50 hover:bg-blue-100/80 text-primary border-blue-200"
                >
                  {showAllLessons ? (
                    <>
                      <MdExpandLess size={16} /> Show Top 3
                    </>
                  ) : (
                    <>
                      <MdExpandMore size={16} /> View All ({myLessons.length})
                    </>
                  )}
                </button>
              )}
            </div>
            
            {isLoadingManage ? (
              <div className="text-center py-8 text-gray-500 font-medium">Loading content...</div>
            ) : myLessons.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl border border-dashed">
                You have not uploaded any lessons yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-400 text-sm font-bold">
                      <th className="pb-3 pr-4">Title</th>
                      <th className="pb-3 px-4">Subject</th>
                      <th className="pb-3 px-4">Format</th>
                      <th className="pb-3 px-4">Status</th>
                      <th className="pb-3 pl-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {displayedLessons.map((lesson) => (
                      <tr key={lesson.id} className="text-darkNavy text-sm hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 pr-4 font-bold max-w-[200px] truncate">{lesson.title}</td>
                        <td className="py-4 px-4 text-gray-500">{getSubjectName(lesson.subjectId)} ({getSubjectClass(lesson.subjectId).toUpperCase()})</td>
                        <td className="py-4 px-4 font-semibold text-gray-600">{lesson.contentData?.lessonType || 'Text'}</td>
                        <td className="py-4 px-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            lesson.status === 'approved' 
                              ? 'bg-green-50 text-green-700 border border-green-200' 
                              : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          }`}>
                            {lesson.status}
                          </span>
                        </td>
                        <td className="py-4 pl-4 text-right flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditLesson(lesson)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Lesson"
                          >
                            <MdEdit size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteLesson(lesson.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Lesson"
                          >
                            <MdDelete size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {myLessons.length > 3 && (
              <div className="pt-2 flex items-center justify-between border-t border-gray-100 text-xs">
                <span className="text-gray-500 font-medium">
                  {showAllLessons 
                    ? `Showing all ${myLessons.length} lessons` 
                    : `Showing 1–${displayedLessons.length} of ${myLessons.length} lessons`}
                </span>
                <button
                  type="button"
                  onClick={() => setShowAllLessons(prev => !prev)}
                  className="font-bold text-primary hover:text-blue-700 flex items-center gap-1 cursor-pointer"
                >
                  {showAllLessons ? 'Show Top 3' : `View All (${myLessons.length}) ➔`}
                </button>
              </div>
            )}
          </div>

          {/* Topics Section */}
          <div className="bg-white p-4 md:p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-darkNavy font-poppins flex items-center gap-2">
                  <MdMenuBook className="text-primary" /> Curriculum & Syllabus Topics ({filteredManageTopics.length})
                </h2>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  {filteredManageTopics.length > 3
                    ? `Showing ${displayedManageTopics.length} of ${filteredManageTopics.length} topics`
                    : 'All topics created in Curriculum Manager and teacher syllabus uploads'}
                </p>
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto">
                {/* Search Bar */}
                <div className="relative flex-1 md:w-64">
                  <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                  <input
                    type="text"
                    value={manageTopicSearch}
                    onChange={(e) => setManageTopicSearch(e.target.value)}
                    placeholder="Search topic or subject..."
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white outline-none transition-all"
                  />
                </div>

                {filteredManageTopics.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllTopics(prev => !prev)}
                    className="px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border shadow-2xs flex-shrink-0 cursor-pointer bg-indigo-50 hover:bg-indigo-100/80 text-indigo-700 border-indigo-200"
                  >
                    {showAllTopics ? (
                      <>
                        <MdExpandLess size={16} /> Top 3
                      </>
                    ) : (
                      <>
                        <MdExpandMore size={16} /> View All ({filteredManageTopics.length})
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Class Filter Chips */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-bold text-gray-500 mr-1">Filter Class:</span>
              {['ALL', 'SS1', 'SS2', 'SS3', 'JSS1', 'JSS2', 'JSS3', 'UTME'].map((cls) => (
                <button
                  key={cls}
                  type="button"
                  onClick={() => setManageClassFilter(cls)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                    manageClassFilter === cls
                      ? 'bg-primary text-white shadow-xs'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cls}
                </button>
              ))}
            </div>
            
            {isLoadingManage ? (
              <div className="text-center py-8 text-gray-500 font-medium">Loading curriculum topics...</div>
            ) : filteredManageTopics.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl border border-dashed">
                {manageTopicSearch || manageClassFilter !== 'ALL'
                  ? 'No curriculum topics match your filter criteria.'
                  : 'You have not created any curriculum topics yet.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-400 text-sm font-bold">
                      <th className="pb-3 pr-4">Topic Title</th>
                      <th className="pb-3 px-4">Subject & Class</th>
                      <th className="pb-3 px-4">Term / Schedule</th>
                      <th className="pb-3 px-4">Source</th>
                      <th className="pb-3 pl-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {displayedManageTopics.map((topic) => {
                      const displayClass = (topic.classId || topic.classLevel || getSubjectClass(topic.subjectId) || '').toUpperCase();
                      const displayName = getSubjectName(topic.subjectId) || topic.subjectName || topic.subjectId;
                      const isCurriculum = topic.source === 'curriculum' || topic.source === 'syllabus_collection';

                      return (
                        <tr key={topic.id} className="text-darkNavy text-sm hover:bg-gray-50/50 transition-colors">
                          <td className="py-4 pr-4 max-w-[280px]">
                            {editingTopicId === topic.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editTopicTitle}
                                  onChange={(e) => setEditTopicTitle(e.target.value)}
                                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/50 w-full"
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleUpdateTopic(topic.id)}
                                  className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex-shrink-0"
                                  title="Save"
                                >
                                  <MdSave size={16} />
                                </button>
                                <button
                                  onClick={() => setEditingTopicId(null)}
                                  className="p-1.5 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 flex-shrink-0"
                                  title="Cancel"
                                >
                                  <MdClose size={16} />
                                </button>
                              </div>
                            ) : (
                              <span className="font-bold block leading-snug">{topic.title}</span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-gray-600 font-semibold text-xs">
                            {displayName} {displayClass ? `(${displayClass})` : ''}
                          </td>
                          <td className="py-4 px-4 text-xs font-medium text-gray-500">
                            <span className="inline-block px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 font-bold text-[11px]">
                              {topic.term || 'Term 1'} {topic.order ? `• W${topic.order}` : ''}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                              isCurriculum
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : 'bg-purple-50 text-purple-700 border border-purple-200'
                            }`}>
                              {topic.sourceLabel || (isCurriculum ? 'Curriculum' : 'Custom')}
                            </span>
                          </td>
                          <td className="py-4 pl-4 text-right flex items-center justify-end gap-2">
                            {editingTopicId !== topic.id && (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingTopicId(topic.id);
                                    setEditTopicTitle(topic.title);
                                  }}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Rename Topic"
                                >
                                  <MdEdit size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeleteTopic(topic)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete Topic"
                                >
                                  <MdDelete size={18} />
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {filteredManageTopics.length > 3 && (
              <div className="pt-2 flex items-center justify-between border-t border-gray-100 text-xs">
                <span className="text-gray-500 font-medium">
                  {showAllTopics 
                    ? `Showing all ${filteredManageTopics.length} topics` 
                    : `Showing 1–${displayedManageTopics.length} of ${filteredManageTopics.length} topics`}
                </span>
                <button
                  type="button"
                  onClick={() => setShowAllTopics(prev => !prev)}
                  className="font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer"
                >
                  {showAllTopics ? 'Show Top 3' : `View All (${filteredManageTopics.length}) ➔`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Document & Curriculum Importer Modal */}
      <AIDocumentImporterModal
        isOpen={isDocImporterOpen}
        onClose={() => setIsDocImporterOpen(false)}
        initialClass={selectedClass.toLowerCase()}
        initialSubject={selectedSubject}
        initialTerm={term}
        userProfile={userProfile}
        isAdmin={isAdmin}
        onImportComplete={() => {
          fetchTopics();
          fetchMyContent();
        }}
      />

      {/* AI Intelligent Multi-Lesson Splitter Modal */}
      <AITopicLessonSplitterModal
        isOpen={isTopicSplitterOpen}
        onClose={() => setIsTopicSplitterOpen(false)}
        topicId={selectedTopic}
        topicTitle={selectedTopicObject ? cleanTopicTitle(selectedTopicObject.title) : ''}
        selectedClass={selectedClass}
        selectedSubject={selectedSubject}
        term={term}
        userProfile={userProfile}
        isAdmin={isAdmin}
        onComplete={() => {
          fetchTopics();
          fetchMyContent();
        }}
      />
    </div>
  );
};

export default TeacherUploadLesson;
